import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Maximize2,
  Move,
  RotateCcw,
  Camera,
  Ruler,
  Tag,
  Save,
  Download,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Lock,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { InfoTooltip } from '@/components/ui/InfoTooltip';
import { ExportCostHint } from '@/features/billing/ExportCostHint';
import { DesignToolbar } from '@/features/designs/DesignToolbar';
import { SaveDesignDialog } from '@/features/designs/SaveDesignDialog';
import { useDesign } from '@/features/designs/useDesigns';
import { useColumnStore } from '@/features/columns/store';
import { useColumnAnalysis, useInteractionSurface } from '@/features/columns/useColumnAnalysis';
import { useExportColumn } from '@/features/columns/useExport';
import { PMSurface3D } from '@/features/columns/PMSurface3D';
import { PMCurve2D } from '@/features/columns/PMCurve2D';
import { Column3DView } from '@/features/columns/Column3DView';
import { AnalysisExplainer } from '@/features/columns/AnalysisExplainer';
import { CuantiaBar } from '@/features/columns/CuantiaBar';
import { HELP } from '@/features/columns/helpText';
import type {
  ColumnAnalyzeRequest,
  InteractionCurve,
  InteractionSurfaceResponse,
  RebarDiam,
  RebarPosition,
  SectionType,
  Verification,
} from '@/features/columns/types';

const REBAR_DIAMS: RebarDiam[] = ['3/8', '1/2', '5/8', '3/4', '1', '1 3/8'];

export function ColumnDesignPage() {
  const mode = useColumnStore((s) => s.mode);
  const view = useColumnStore((s) => s.view);
  const request = useColumnStore((s) => s.request);
  const setMode = useColumnStore((s) => s.setMode);
  const setView = useColumnStore((s) => s.setView);
  const setSectionType = useColumnStore((s) => s.setSectionType);
  const setSection = useColumnStore((s) => s.setSection);
  const setMaterials = useColumnStore((s) => s.setMaterials);
  const setLoads = useColumnStore((s) => s.setLoads);
  const setReinforcement = useColumnStore((s) => s.setReinforcement);
  const loadRequest = useColumnStore((s) => s.loadRequest);

  // URL hydration: /app/columns/{id} loads that saved design into the store.
  // `new` is treated as no-id (blank form using current defaults).
  const params = useParams<{ id?: string }>();
  const designId =
    params.id && params.id !== 'new' && /^\d+$/.test(params.id) ? Number(params.id) : null;
  const designQuery = useDesign(designId);
  useEffect(() => {
    if (designQuery.data) {
      // Pydantic-side schema may have evolved between save and now —
      // trust the saved payload and let the next analysis call surface
      // any validation issue.
      loadRequest(designQuery.data.payload as unknown as ColumnAnalyzeRequest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designQuery.data?.id]);

  const [saveOpen, setSaveOpen] = useState(false);

  const exporter = useExportColumn();
  const { data, isLoading, isFetching, isError, error } = useColumnAnalysis(request);
  const { data: surface, isFetching: surfaceFetching } = useInteractionSurface(
    {
      section: request.section,
      materials: request.materials,
      reinforcement: request.reinforcement,
      n_angular: 24,
      n_points: 30,
    },
    mode === 'analysis',
  );

  return (
    <div className="flex h-full flex-col">
      <ProjectHeader status={isError ? 'error' : isFetching ? 'calculating' : 'ok'} errorMsg={String(error ?? '')} />
      {/* Standardised toolbar across all design pages — status + PDF/XLSX
          + Save button. The "Save" button opens SaveDesignDialog for both
          new and update modes (the latter when the URL contains an id). */}
      {/* Each section type is treated as an independent design: switching
          from Rect to L while on a saved /:id route turns the "Actualizar"
          button into "Guardar" (create new) because we're no longer
          editing the original saved design. */}
      {(() => {
        // Same identity check used by the dialog below — kept as a single
        // const so the toolbar label and the save semantics never disagree.
        const matchingDesign =
          designQuery.data && designQuery.data.subtype === request.section.type
            ? designQuery.data
            : null;
        return (
          <>
            <DesignToolbar
              status={isError ? 'error' : isFetching ? 'busy' : 'ready'}
              statusLabel={isError ? 'Revisa los datos' : undefined}
              onExportPdf={() => exporter.trigger('pdf', request)}
              onExportXlsx={() => exporter.trigger('xlsx', request)}
              busyExport={exporter.busy}
              onSave={() => setSaveOpen(true)}
              isUpdate={!!matchingDesign}
            />
            <SaveDesignDialog
              open={saveOpen}
              onOpenChange={setSaveOpen}
              module="columns"
              subtype={request.section.type}
              payload={request as unknown as Record<string, unknown>}
              existing={matchingDesign}
            />
          </>
        );
      })()}

      <div className="flex flex-1 overflow-hidden">
        <LeftRail
          sectionType={request.section.type}
          onSectionTypeChange={setSectionType}
          L1={request.section.L1}
          L2={request.section.L2}
          H_clear={request.section.H_clear}
          e1={request.section.e1}
          e2={request.section.e2}
          onL1={(v) => setSection({ L1: v })}
          onL2={(v) => setSection({ L2: v })}
          onH={(v) => setSection({ H_clear: v })}
          onE1={(v) => setSection({ e1: v })}
          onE2={(v) => setSection({ e2: v })}
          fc={request.materials.fc}
          fy={request.materials.fy}
          cover={request.materials.cover}
          onMat={setMaterials}
          Pu={request.loads.Pu}
          Mu_x={request.loads.Mu_x}
          Mu_y={request.loads.Mu_y}
          Vu_x={request.loads.Vu_x}
          Vu_y={request.loads.Vu_y}
          onLoads={setLoads}
          rebar={request.reinforcement}
          onReinf={setReinforcement}
        />

        <section className="flex flex-1 flex-col overflow-hidden">
          <ModeTabs mode={mode} onChange={setMode} surfaceFetching={surfaceFetching} />
          {mode === 'design' ? (
            <Canvas
              view={view}
              setView={setView}
              mode={mode}
              sectionType={request.section.type}
              L1={request.section.L1}
              L2={request.section.L2}
              H_clear={request.section.H_clear}
              e1={request.section.e1}
              e2={request.section.e2}
              rebars={data?.rebar_positions ?? []}
              stirrups={data?.stirrups}
              cover={request.materials.cover}
              isLoading={isLoading}
            />
          ) : (
            <AnalysisView
              surface={surface}
              curveX={data?.interaction_x}
              curveY={data?.interaction_y}
              Pu={request.loads.Pu}
              Mu_x={request.loads.Mu_x}
              Mu_y={request.loads.Mu_y}
              L1={request.section.L1}
              L2={request.section.L2}
              fc={request.materials.fc}
            />
          )}
        </section>

        <RightRail
          mode={mode}
          outputs={data?.outputs}
          stirrups={data?.stirrups}
          shear={data?.shear}
          flexocomp={data?.flexocompression}
          dcRatio={data?.dc_ratio}
          verifications={data?.verifications ?? []}
          isFetching={isFetching}
          onExportPdf={() => exporter.trigger('pdf', request)}
          onExportXlsx={() => exporter.trigger('xlsx', request)}
          exportBusy={exporter.busy}
        />
      </div>
    </div>
  );
}

function AnalysisView({
  surface,
  curveX,
  curveY,
  Pu,
  Mu_x,
  Mu_y,
  L1,
  L2,
  fc,
}: {
  surface: InteractionSurfaceResponse | undefined;
  curveX: InteractionCurve | undefined;
  curveY: InteractionCurve | undefined;
  Pu: number;
  Mu_x: number;
  Mu_y: number;
  L1: number;
  L2: number;
  fc: number;
}) {
  return (
    <div className="flex flex-1 flex-col gap-3 overflow-hidden p-3" style={{ background: 'var(--surface-canvas)' }}>
      <div
        className="relative flex-1 overflow-hidden rounded-[var(--radius-md)] border"
        style={{ borderColor: 'var(--border-default)' }}
      >
        {surface ? (
          <PMSurface3D
            surface={surface}
            Pu={Pu}
            Mu_x={Mu_x}
            Mu_y={Mu_y}
            envelope="design"
            view="wireframe"
            L1={L1}
            L2={L2}
            fc={fc}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-xs" style={{ color: 'var(--text-muted)' }}>
            Construyendo solido P-Mx-My...
          </div>
        )}
        <div
          className="absolute left-3 top-3 rounded-[var(--radius-sm)] border px-2 py-1 text-[10px] uppercase tracking-[0.18em]"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)', color: 'var(--text-muted)' }}
        >
          Diagrama de interaccion 3D - P-Mx-My
        </div>
      </div>

      <div className="grid h-72 shrink-0 grid-cols-2 gap-3">
        <div
          className="rounded-[var(--radius-md)] border p-3"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          <PMCurve2D
            curve={curveX}
            demand={{ M: Mu_x, P: Pu }}
            title="P − Mx · sismo X"
            subtitle="corte plano del sólido P-Mx-My con My = 0"
            height={230}
          />
        </div>
        <div
          className="rounded-[var(--radius-md)] border p-3"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          <PMCurve2D
            curve={curveY}
            demand={{ M: Mu_y, P: Pu }}
            title="P − My · sismo Y"
            subtitle="corte plano del sólido P-Mx-My con Mx = 0"
            height={230}
          />
        </div>
      </div>

      <AnalysisExplainer />
    </div>
  );
}

function ProjectHeader({ status, errorMsg }: { status: 'ok' | 'calculating' | 'error'; errorMsg: string }) {
  return (
    <header
      className="flex h-14 shrink-0 items-center gap-6 border-b px-6"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
    >
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
          Software E.060
        </div>
        <div
          className="text-sm leading-none"
          style={{ fontFamily: 'var(--font-display)', fontWeight: 700, letterSpacing: '-0.01em' }}
        >
          Diseno profesional de columnas
        </div>
      </div>

      <div className="ml-6 flex items-center gap-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
        <span style={{ color: 'var(--text-muted)' }}>Proyecto:</span>
        <button className="rounded px-2 py-1 hover:bg-[var(--surface-2)]" style={{ color: 'var(--brand-alt)' }}>
          NUEVO PROYECTO ESTRUCTURAL
        </button>
      </div>
      {/* Right side intentionally empty — the DesignToolbar below shows
          the calculation status, so duplicating it here just took space
          (the previous "Licencia: 12345678" was placeholder filler). */}
    </header>
  );
}

function StatusBadge({ status, errorMsg }: { status: 'ok' | 'calculating' | 'error'; errorMsg: string }) {
  if (status === 'error') {
    return (
      <span
        className="flex items-center gap-1.5 rounded-[var(--radius-pill)] border px-2 py-1 text-[11px]"
        style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}
        title={errorMsg}
      >
        <AlertCircle size={12} strokeWidth={2} /> Error
      </span>
    );
  }
  if (status === 'calculating') {
    return (
      <span
        className="flex items-center gap-1.5 rounded-[var(--radius-pill)] border px-2 py-1 text-[11px]"
        style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}
      >
        <Loader2 size={12} strokeWidth={2} className="animate-spin" /> Calculando
      </span>
    );
  }
  return (
    <span
      className="flex items-center gap-1.5 rounded-[var(--radius-pill)] border px-2 py-1 text-[11px]"
      style={{ borderColor: 'var(--border-default)', color: 'var(--success)' }}
    >
      <CheckCircle2 size={12} strokeWidth={2} /> Actualizado
    </span>
  );
}

function ModeTabs({
  mode,
  onChange,
  surfaceFetching,
}: {
  mode: 'design' | 'analysis';
  onChange: (m: 'design' | 'analysis') => void;
  surfaceFetching?: boolean;
}) {
  return (
    <div
      className="flex shrink-0 items-center gap-2 border-b px-4 py-2"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
    >
      <Button variant={mode === 'design' ? 'primary' : 'ghost'} size="sm" onClick={() => onChange('design')}>
        Diseno de seccion
      </Button>
      <Button variant={mode === 'analysis' ? 'primary' : 'ghost'} size="sm" onClick={() => onChange('analysis')}>
        Analisis de interaccion
      </Button>
      {mode === 'analysis' && surfaceFetching && (
        <span className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
          <Loader2 size={11} className="animate-spin" /> construyendo solido 3D
        </span>
      )}
      <div className="ml-auto flex items-center gap-2">
        <Button variant="ghost" size="icon" aria-label="Guardar">
          <Save size={14} strokeWidth={1.8} />
        </Button>
      </div>
    </div>
  );
}

interface LeftRailProps {
  sectionType: SectionType;
  onSectionTypeChange: (s: SectionType) => void;
  L1: number;
  L2: number;
  H_clear: number;
  e1: number;
  e2: number;
  onL1: (v: number) => void;
  onL2: (v: number) => void;
  onH: (v: number) => void;
  onE1: (v: number) => void;
  onE2: (v: number) => void;
  fc: number;
  fy: number;
  cover: number;
  onMat: (patch: Partial<{ fc: number; fy: number; cover: number }>) => void;
  Pu: number;
  Mu_x: number;
  Mu_y: number;
  Vu_x: number;
  Vu_y: number;
  onLoads: (patch: Partial<{ Pu: number; Mu_x: number; Mu_y: number; Vu_x: number; Vu_y: number }>) => void;
  rebar: ReturnType<typeof useColumnStore.getState>['request']['reinforcement'];
  onReinf: (patch: Partial<ReturnType<typeof useColumnStore.getState>['request']['reinforcement']>) => void;
}

function LeftRail(p: LeftRailProps) {
  return (
    <aside
      className="flex w-80 shrink-0 flex-col overflow-y-auto border-r p-4 scrollbar-thin"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
    >
      <Group title="Tipo de seccion" help={HELP.sectionType}>
        <SectionTypePicker value={p.sectionType} onChange={p.onSectionTypeChange} />
      </Group>

      <Group title="Materiales">
        <NumberRow label="f'c (kgf/cm²)" help={HELP.fc} value={p.fc} onChange={(v) => p.onMat({ fc: v })} />
        <NumberRow label="fy (kgf/cm²)" help={HELP.fy} value={p.fy} onChange={(v) => p.onMat({ fy: v })} />
        <NumberRow label="Recubrimiento (cm)" help={HELP.cover} value={p.cover} onChange={(v) => p.onMat({ cover: v })} step={0.5} />
      </Group>

      <Group title="Geometria">
        {p.sectionType === 'circular' ? (
          <NumberRow label="D (cm) — diametro" help={HELP.L1} value={p.L1} onChange={p.onL1} />
        ) : (
          <>
            <NumberRow label="L1 (cm)" help={HELP.L1} value={p.L1} onChange={p.onL1} />
            <NumberRow label="L2 (cm)" help={HELP.L2} value={p.L2} onChange={p.onL2} />
          </>
        )}
        <NumberRow label="H libre (cm)" help={HELP.H_clear} value={p.H_clear} onChange={p.onH} step={5} />
        {p.sectionType === 'L' && (
          <>
            <NumberRow label="e1 (cm) — ala inferior" help={HELP.e1} value={p.e1} onChange={p.onE1} step={1} />
            <NumberRow label="e2 (cm) — alma izq." help={HELP.e2} value={p.e2} onChange={p.onE2} step={1} />
          </>
        )}
        {p.sectionType === 'T' && (
          <>
            <NumberRow label="e1 (cm) — ala superior" help={HELP.e1} value={p.e1} onChange={p.onE1} step={1} />
            <NumberRow label="e2 (cm) — alma central" help={HELP.e2} value={p.e2} onChange={p.onE2} step={1} />
          </>
        )}
        {p.sectionType === 'cruciforme' && (
          <>
            <NumberRow label="e1 (cm) — brazo horizontal" help={HELP.e1} value={p.e1} onChange={p.onE1} step={1} />
            <NumberRow label="e2 (cm) — brazo vertical" help={HELP.e2} value={p.e2} onChange={p.onE2} step={1} />
          </>
        )}
      </Group>

      <Group title="Cargas (kgf, kgf·cm)" help={HELP.loadsDisclaimer}>
        <div
          className="mb-2 flex items-start gap-1.5 rounded-[var(--radius-sm)] border px-2 py-1.5 text-[10px]"
          style={{
            background: 'rgba(59,130,246,0.08)',
            borderColor: 'rgba(59,130,246,0.30)',
            color: 'var(--text-secondary)',
            lineHeight: 1.4,
          }}
        >
          <Info size={11} strokeWidth={1.8} className="mt-0.5 shrink-0" style={{ color: 'var(--brand-alt)' }} />
          <span>
            <span style={{ color: 'var(--brand-alt)', fontWeight: 600 }}>Valores de ejemplo.</span> Reemplaza
            con los de tu analisis estructural (ETABS, SAP, manual) en el caso de carga mas critico.
          </span>
        </div>
        <NumberRow label="Pu" help={HELP.Pu} value={p.Pu} onChange={(v) => p.onLoads({ Pu: v })} />
        <NumberRow label="Mu22 (Mu_x)" help={HELP.Mu_x} value={p.Mu_x} onChange={(v) => p.onLoads({ Mu_x: v })} />
        <NumberRow label="Mu33 (Mu_y)" help={HELP.Mu_y} value={p.Mu_y} onChange={(v) => p.onLoads({ Mu_y: v })} />
        <NumberRow label="Vu_x" help={HELP.Vu_x} value={p.Vu_x} onChange={(v) => p.onLoads({ Vu_x: v })} />
        <NumberRow label="Vu_y" help={HELP.Vu_y} value={p.Vu_y} onChange={(v) => p.onLoads({ Vu_y: v })} />
      </Group>

      <Group title="Acero longitudinal" help={HELP.totalBars}>
        {p.sectionType === 'circular' ? (
          <RebarBlock
            title="Barras del aro"
            subtitle="todas iguales, distribuidas en el circulo"
            count={p.rebar.n_circular_bars ?? 8}
            onCount={(n) => p.onReinf({ n_circular_bars: n })}
            diam={p.rebar.diam_corners}
            onDiam={(d) => p.onReinf({ diam_corners: d })}
            color="var(--accent)"
            help={HELP.diam_corners}
          />
        ) : (
          <>
            <RebarBlock
              title="Esquinas"
              subtitle={
                p.sectionType === 'L'
                  ? '6 barras (5 convexas + 1 re-entrante)'
                  : p.sectionType === 'T'
                    ? '8 barras (6 convexas + 2 re-entrantes)'
                    : p.sectionType === 'cruciforme'
                      ? '12 barras (8 convexas + 4 re-entrantes)'
                      : '4 barras'
              }
              countLocked={
                p.sectionType === 'L' ? 6
                : p.sectionType === 'T' ? 8
                : p.sectionType === 'cruciforme' ? 12
                : 4
              }
              diam={p.rebar.diam_corners}
              onDiam={(d) => p.onReinf({ diam_corners: d })}
              color="var(--accent)"
              help={HELP.diam_corners}
            />
            <RebarBlock
              title="Intermedias L1"
              subtitle="entre esquinas (top y bot)"
              count={p.rebar.n_intermediate_L1}
              onCount={(n) => p.onReinf({ n_intermediate_L1: n })}
              diam={p.rebar.diam_intermediate_L1}
              onDiam={(d) => p.onReinf({ diam_intermediate_L1: d })}
              color="var(--rebar-longitudinal)"
              help={HELP.n_intermediate_L1}
            />
            <RebarBlock
              title="Intermedias L2"
              subtitle="entre esquinas (izq y der)"
              count={p.rebar.n_intermediate_L2}
              onCount={(n) => p.onReinf({ n_intermediate_L2: n })}
              diam={p.rebar.diam_intermediate_L2}
              onDiam={(d) => p.onReinf({ diam_intermediate_L2: d })}
              color="var(--brand-violet)"
              help={HELP.n_intermediate_L2}
            />
          </>
        )}
        <div
          className="mt-1 rounded-[var(--radius-sm)] border px-2 py-1.5 text-center text-[11px] font-mono tabular"
          style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}
        >
          {p.sectionType === 'L' ? (
            <>
              Total ≈ <span style={{ color: 'var(--accent)', fontWeight: 700 }}>6</span> esquinas + intermedias
              <div className="mt-0.5 text-[9.5px]" style={{ color: 'var(--text-muted)' }}>
                (5 vertices convexos del L + 1 acero en la esquina re-entrante de 270° para amarre del estribo; las intermedias L1/L2 pueden generar 1 o 2 según geometría)
              </div>
            </>
          ) : p.sectionType === 'T' ? (
            <>
              Total ≈ <span style={{ color: 'var(--accent)', fontWeight: 700 }}>8</span> esquinas + intermedias
              <div className="mt-0.5 text-[9.5px]" style={{ color: 'var(--text-muted)' }}>
                (6 vertices convexos del T + 2 aceros en las esquinas re-entrantes alma/ala para amarre del estribo; las L1 generan 1 ó 2 según pasen sobre el alma; las L2 colocan 2 barras por nivel — alma izq + alma der)
              </div>
            </>
          ) : p.sectionType === 'cruciforme' ? (
            <>
              Total ≈ <span style={{ color: 'var(--accent)', fontWeight: 700 }}>12</span> esquinas + intermedias
              <div className="mt-0.5 text-[9.5px]" style={{ color: 'var(--text-muted)' }}>
                (8 vertices convexos de los 4 brazos + 4 aceros en las esquinas re-entrantes del cruce para amarre; las L1 colocan barras en los flancos del brazo horizontal — 2 por nivel; las L2 idem en los flancos del brazo vertical)
              </div>
            </>
          ) : p.sectionType === 'circular' ? (
            <>
              Total = <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{p.rebar.n_circular_bars ?? 8}</span> barras distribuidas en el aro
              <div className="mt-0.5 text-[9.5px]" style={{ color: 'var(--text-muted)' }}>
                (todas del mismo diametro; se distribuyen uniformemente en un circulo de radio R = D/2 − recubrimiento − d_estribo − d_barra/2)
              </div>
            </>
          ) : (
            <>
              Total = 4 + 2 × {p.rebar.n_intermediate_L1} + 2 × {p.rebar.n_intermediate_L2} ={' '}
              <span style={{ color: 'var(--accent)', fontWeight: 700 }}>
                {4 + 2 * p.rebar.n_intermediate_L1 + 2 * p.rebar.n_intermediate_L2}
              </span>{' '}
              barras
            </>
          )}
        </div>
      </Group>
    </aside>
  );
}

function RebarBlock({
  title,
  subtitle,
  count,
  countLocked,
  onCount,
  diam,
  onDiam,
  color,
  help,
}: {
  title: string;
  subtitle: string;
  count?: number;
  countLocked?: number;
  onCount?: (n: number) => void;
  diam: RebarDiam;
  onDiam: (d: RebarDiam) => void;
  color: string;
  help?: string;
}) {
  const displayCount = countLocked ?? count ?? 0;
  return (
    <div
      className="rounded-[var(--radius-sm)] border p-2"
      style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', borderLeft: `3px solid ${color}` }}
    >
      <div className="mb-1.5 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-1.5 text-[11px]" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
            <span className="inline-block h-2 w-2 rounded-full" style={{ background: color }} />
            {title}
            {help && <InfoTooltip content={help} side="right" />}
          </div>
          <div className="text-[9.5px]" style={{ color: 'var(--text-muted)' }}>
            {subtitle}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-0.5">
          <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
            Cant.
          </span>
          {countLocked !== undefined ? (
            <div
              className="flex h-7 items-center justify-center rounded-[var(--radius-sm)] border font-mono tabular text-xs"
              style={{
                background: 'var(--surface-3)',
                borderColor: 'var(--border-default)',
                color: 'var(--text-disabled)',
              }}
              title="Las esquinas siempre son 4"
            >
              {countLocked} (fijo)
            </div>
          ) : (
            <input
              type="number"
              min={0}
              value={count ?? 0}
              onChange={(e) => onCount?.(Math.max(0, e.currentTarget.valueAsNumber || 0))}
              className="h-7 rounded-[var(--radius-sm)] border bg-[var(--surface-1)] px-2 text-right font-mono tabular text-xs text-[var(--text-primary)] focus:border-[var(--accent)] focus:outline-none"
              style={{ borderColor: 'var(--border-default)' }}
            />
          )}
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
            Ø
          </span>
          <select
            value={diam}
            onChange={(e) => onDiam(e.currentTarget.value as RebarDiam)}
            disabled={countLocked === 0 || (countLocked === undefined && (count ?? 0) === 0)}
            className="h-7 rounded-[var(--radius-sm)] border bg-[var(--surface-1)] px-1 font-mono tabular text-xs text-[var(--text-primary)] focus:border-[var(--accent)] focus:outline-none disabled:opacity-50"
            style={{ borderColor: 'var(--border-default)' }}
          >
            {REBAR_DIAMS.map((d) => (
              <option key={d} value={d}>
                {d}&quot;
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="mt-1.5 text-[9.5px] font-mono tabular" style={{ color: 'var(--text-muted)' }}>
        {countLocked !== undefined ? '4 barras' : `2 × ${displayCount} = ${2 * displayCount} barras`} de ø{diam}&quot;
      </div>
    </div>
  );
}

function Group({ title, help, children }: { title: string; help?: string; children: React.ReactNode }) {
  return (
    <div className="mb-5">
      <div className="mb-2 flex items-center gap-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
          {title}
        </span>
        {help && <InfoTooltip content={help} side="right" />}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function NumberRow({
  label,
  help,
  value,
  onChange,
  step = 1,
}: {
  label: string;
  help?: string;
  value: number;
  onChange: (v: number) => void;
  step?: number;
}) {
  return (
    <label className="flex items-center justify-between gap-3">
      <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
        {label}
        {help && <InfoTooltip content={help} side="right" />}
      </span>
      <input
        type="number"
        value={value}
        step={step}
        onChange={(e) => {
          const v = e.currentTarget.valueAsNumber;
          if (!Number.isNaN(v)) onChange(v);
        }}
        className="h-8 w-28 rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-2 text-right font-mono tabular text-xs text-[var(--text-primary)] focus:border-[var(--accent)] focus:outline-none"
        style={{ borderColor: 'var(--border-default)' }}
      />
    </label>
  );
}

const SECTION_TYPES: { id: SectionType; label: string; available: boolean }[] = [
  { id: 'rectangular', label: 'Rect', available: true },
  { id: 'L', label: 'L', available: true },
  { id: 'T', label: 'T', available: true },
  { id: 'cruciforme', label: '+', available: true },
  { id: 'circular', label: 'O', available: true },
];

function SectionTypePicker({ value, onChange }: { value: SectionType; onChange: (s: SectionType) => void }) {
  return (
    <div
      className="grid grid-cols-5 overflow-hidden rounded-[var(--radius-sm)] border"
      style={{ borderColor: 'var(--border-default)', background: 'var(--surface-2)' }}
    >
      {SECTION_TYPES.map((t) => (
        <button
          key={t.id}
          onClick={() => t.available && onChange(t.id)}
          disabled={!t.available}
          className="h-9 text-xs font-medium transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-40"
          style={{
            background: value === t.id ? 'var(--accent)' : 'transparent',
            color: value === t.id ? 'var(--surface-base)' : 'var(--text-secondary)',
          }}
          title={!t.available ? 'Proximamente' : undefined}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}


function Canvas({
  view,
  setView,
  mode,
  sectionType,
  L1,
  L2,
  H_clear,
  e1,
  e2,
  rebars,
  stirrups,
  cover,
  isLoading,
}: {
  view: '2D' | '3D';
  setView: (v: '2D' | '3D') => void;
  mode: 'design' | 'analysis';
  sectionType: SectionType;
  L1: number;
  L2: number;
  H_clear: number;
  e1: number;
  e2: number;
  rebars: RebarPosition[];
  stirrups?: import('@/features/columns/types').StirrupOutputs;
  cover: number;
  isLoading: boolean;
}) {
  return (
    <div className="relative flex-1" style={{ background: 'var(--surface-canvas)' }}>
      <CanvasToolbar view={view} setView={setView} />
      {view === '3D' ? (
        <div className="absolute inset-0">
          <Column3DView
            sectionType={sectionType}
            L1={L1}
            L2={L2}
            e1={e1}
            e2={e2}
            height={H_clear}
            rebars={rebars}
            stirrups={stirrups}
            cover={cover}
          />
          <div
            className="pointer-events-none absolute left-3 top-3 rounded-[var(--radius-sm)] border px-2 py-1 text-[10px] uppercase tracking-[0.18em]"
            style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)', color: 'var(--text-muted)' }}
          >
            Vista 3D - columna {L1}x{L2} cm, H = {H_clear} cm
            {stirrups && (
              <>
                {' · '}
                <span style={{ color: 'var(--rebar-stirrup)' }}>
                  {stirrups.n_confinement_each_end}@{stirrups.spacing_confinement.toFixed(0)} | {stirrups.n_central}@{stirrups.spacing_central.toFixed(0)} | {stirrups.n_confinement_each_end}@{stirrups.spacing_confinement.toFixed(0)}
                </span>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          {isLoading ? (
            <div className="flex flex-col items-center gap-2" style={{ color: 'var(--text-muted)' }}>
              <Loader2 size={24} className="animate-spin" />
              <span className="text-xs">Calculando seccion...</span>
            </div>
          ) : (
            <SectionRender
              L1={L1}
              L2={L2}
              rebars={rebars}
              sectionType={sectionType}
              e1={e1}
              e2={e2}
              cover={cover}
              stirrupDiameter={stirrups?.diameter_in === '3/8' ? 0.95 : 0.95}
            />
          )}
        </div>
      )}
      {mode === 'analysis' && <ParametersDock />}
    </div>
  );
}

function CanvasToolbar({ view, setView }: { view: '2D' | '3D'; setView: (v: '2D' | '3D') => void }) {
  return (
    <div
      className="absolute right-4 top-4 z-10 flex items-center gap-1 rounded-[var(--radius)] border p-1 shadow-md"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
    >
      <div className="mr-1 flex overflow-hidden rounded-[var(--radius-sm)] border" style={{ borderColor: 'var(--border-default)' }}>
        {(['2D', '3D'] as const).map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className="h-7 px-3 text-[11px] font-medium"
            style={{
              background: view === v ? 'var(--accent)' : 'transparent',
              color: view === v ? 'var(--surface-base)' : 'var(--text-secondary)',
            }}
          >
            {v}
          </button>
        ))}
      </div>
      <Button variant="ghost" size="icon" aria-label="Pan"><Move size={14} strokeWidth={1.8} /></Button>
      <Button variant="ghost" size="icon" aria-label="Fit"><Maximize2 size={14} strokeWidth={1.8} /></Button>
      <Button variant="ghost" size="icon" aria-label="Reset"><RotateCcw size={14} strokeWidth={1.8} /></Button>
      <Button variant="ghost" size="icon" aria-label="Snapshot"><Camera size={14} strokeWidth={1.8} /></Button>
      <Button variant="ghost" size="icon" aria-label="Cotas"><Ruler size={14} strokeWidth={1.8} /></Button>
      <Button variant="ghost" size="icon" aria-label="Etiquetas"><Tag size={14} strokeWidth={1.8} /></Button>
    </div>
  );
}

function SectionRender({
  L1,
  L2,
  rebars,
  sectionType,
  e1,
  e2,
  cover,
  stirrupDiameter,
}: {
  L1: number;
  L2: number;
  rebars: RebarPosition[];
  sectionType: SectionType;
  e1: number;
  e2: number;
  cover: number;
  stirrupDiameter: number;
}) {
  // Drawing area + offsets for external dimension chains (top/bot/left/right).
  const SIZE = 480;
  const PAD = 70; // room for dim chains around the section
  const isL = sectionType === 'L';
  const isT = sectionType === 'T';
  const isPlus = sectionType === 'cruciforme';
  const isCirc = sectionType === 'circular';

  // World-space bbox of the section (centered at section centroid)
  const polygon = isL
    ? lPolygonRaw(L1, L2, e1, e2)
    : isT
      ? tPolygonRaw(L1, L2, e1, e2)
      : isPlus
        ? cruciformPolygonRaw(L1, L2, e1, e2)
        : isCirc
          ? circularPolygonRaw(L1)
          : ([
              [-L1 / 2, -L2 / 2],
              [L1 / 2, -L2 / 2],
              [L1 / 2, L2 / 2],
              [-L1 / 2, L2 / 2],
            ] as const);
  const xs = polygon.map((p) => p[0]);
  const ys = polygon.map((p) => p[1]);
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);
  const wWorld = xMax - xMin;
  const hWorld = yMax - yMin;

  const scale = Math.min((SIZE - 2 * PAD) / wWorld, (SIZE - 2 * PAD) / hWorld);
  const cx = SIZE / 2;
  const cy = SIZE / 2;

  // SVG transforms (engineering y up -> SVG y down)
  const sx = (x: number) => cx + x * scale;
  const sy = (y: number) => cy - y * scale;

  // Polygon path for the concrete
  const polyPts = polygon.map(([x, y]) => `${sx(x)},${sy(y)}`).join(' ');

  // Stirrup outline. Drawing convention: the stirrup polyline passes through
  // the CENTER of the corner longitudinal bars (so it visually "ties" them).
  // That position is the same offset used by the backend to place corner bars:
  //     corner_offset = cover + d_stirrup + d_corner_bar / 2
  // We approximate d_corner_bar from the largest 'corner' rebar area present
  // in `rebars`; fall back to the stirrup centerline (cover + d_stirrup/2) if
  // no corner bars yet (early render).
  const cornerAreas = rebars.filter((r) => r.kind === 'corner').map((r) => r.area);
  const cornerBarArea = cornerAreas.length > 0 ? Math.max(...cornerAreas) : 0;
  const cornerBarDiameter = cornerBarArea > 0 ? 2 * Math.sqrt(cornerBarArea / Math.PI) : 0;
  const stirrupInset = cornerBarDiameter > 0
    ? cover + stirrupDiameter + cornerBarDiameter / 2
    : cover + stirrupDiameter / 2;
  const stirrupPts = insetPolygonInPlace(polygon, stirrupInset);

  const colorOf = (kind: RebarPosition['kind']): string =>
    kind === 'corner'
      ? 'var(--accent)'
      : kind === 'intermediate_L1'
        ? 'var(--rebar-longitudinal)'
        : 'var(--brand-violet)';

  // ===== Compute bar spacing chains for each face =====
  const tol = 0.4;
  // Top row (y ≈ yMax) — sort by x
  const topBars = rebars.filter((r) => Math.abs(r.y - yMax) < tol).sort((a, b) => a.x - b.x);
  // Bottom row (y ≈ yMin)
  const botBars = rebars.filter((r) => Math.abs(r.y - yMin) < tol).sort((a, b) => a.x - b.x);
  // Left col (x ≈ xMin)
  const leftBars = rebars.filter((r) => Math.abs(r.x - xMin) < tol).sort((a, b) => a.y - b.y);
  // Right col (x ≈ xMax)
  const rightBars = rebars.filter((r) => Math.abs(r.x - xMax) < tol).sort((a, b) => a.y - b.y);

  return (
    <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="hatch" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--text-muted)" strokeOpacity="0.20" strokeWidth="1" />
        </pattern>
      </defs>

      {/* Concrete outline + hatch */}
      <polygon points={polyPts} fill="url(#hatch)" stroke="var(--concrete-outline)" strokeWidth="1.6" />

      {/* Stirrup */}
      <StirrupWithHooks
        polygon={stirrupPts}
        sx={sx}
        sy={sy}
        scale={scale}
        hookLengthCm={Math.max(3, 6 * stirrupDiameter)}
        noHooks={isCirc}
      />

      {/* Bars */}
      {rebars.map((r, i) => {
        const px = sx(r.x);
        const py = sy(r.y);
        const radius_cm = Math.sqrt(r.area / Math.PI);
        const pr = Math.max(3.5, radius_cm * scale);
        return (
          <circle
            key={i}
            cx={px}
            cy={py}
            r={pr}
            fill={colorOf(r.kind)}
            stroke="var(--concrete-outline)"
            strokeWidth="0.6"
          />
        );
      })}

      {/* External dimension chains (skipped for circular — circular shows only diameter) */}
      {!isCirc && <DimChain
        orientation="horizontal"
        anchor={sy(yMax) - 18}                    /* sits above the top edge */
        positionsWorld={topBars.map((b) => b.x)}
        edgeMin={xMin}
        edgeMax={xMax}
        sx={sx}
        scale={scale}
        labelStart={null}
        labelEnd={null}
      />}
      {!isCirc && <DimChain
        orientation="horizontal"
        anchor={sy(yMin) + 18}                     /* sits below the bottom edge */
        positionsWorld={botBars.map((b) => b.x)}
        edgeMin={xMin}
        edgeMax={xMax}
        sx={sx}
        scale={scale}
      />}
      {!isCirc && <DimChainV
        anchor={sx(xMin) - 18}                     /* sits left of the left edge */
        positionsWorld={leftBars.map((b) => b.y)}
        edgeMin={yMin}
        edgeMax={yMax}
        sy={sy}
        scale={scale}
      />}
      {!isCirc && <DimChainV
        anchor={sx(xMax) + 18}                     /* sits right of the right edge */
        positionsWorld={rightBars.map((b) => b.y)}
        edgeMin={yMin}
        edgeMax={yMax}
        sy={sy}
        scale={scale}
      />}

      {/* Total dimensions (outside the spacing chains) */}
      <DimTotal
        orientation="horizontal"
        anchor={sy(yMin) + 44}
        from={sx(xMin)}
        to={sx(xMax)}
        label={
          isCirc
            ? `D = ${L1} cm`
            : isL || isT || isPlus
              ? `L1 = ${L1} cm`
              : `b = ${L1} cm`
        }
      />
      {!isCirc && (
        <DimTotal
          orientation="vertical"
          anchor={sx(xMin) - 44}
          from={sy(yMin)}
          to={sy(yMax)}
          label={isL || isT || isPlus ? `L2 = ${L2} cm` : `h = ${L2} cm`}
        />
      )}

      {/* Extra dimensions for L: e1 right (flange thickness), e2 top (web thickness) */}
      {isL && (
        <>
          <DimTotal
            orientation="vertical"
            anchor={sx(xMax) + 44}
            from={sy(yMin)}
            to={sy(yMin + e1)}
            label={`e1 = ${e1}`}
            color="var(--accent-soft)"
          />
          <DimTotal
            orientation="horizontal"
            anchor={sy(yMax) - 44}
            from={sx(xMin)}
            to={sx(xMin + e2)}
            label={`e2 = ${e2}`}
            color="var(--accent-soft)"
          />
        </>
      )}
      {/* Extra dimensions for T: e1 = flange thickness (right), e2 = web thickness (top) */}
      {isT && (
        <>
          <DimTotal
            orientation="vertical"
            anchor={sx(xMax) + 44}
            from={sy(yMax - e1)}
            to={sy(yMax)}
            label={`e1 = ${e1}`}
            color="var(--accent-soft)"
          />
          <DimTotal
            orientation="horizontal"
            anchor={sy(yMax) + 44}
            from={sx(-e2 / 2)}
            to={sx(+e2 / 2)}
            label={`e2 = ${e2}`}
            color="var(--accent-soft)"
          />
        </>
      )}
      {/* Extra dimensions for +: e1 = horizontal arm thickness (right), e2 = vertical arm thickness (top) */}
      {isPlus && (
        <>
          <DimTotal
            orientation="vertical"
            anchor={sx(xMax) + 44}
            from={sy(-e1 / 2)}
            to={sy(+e1 / 2)}
            label={`e1 = ${e1}`}
            color="var(--accent-soft)"
          />
          <DimTotal
            orientation="horizontal"
            anchor={sy(yMax) + 44}
            from={sx(-e2 / 2)}
            to={sx(+e2 / 2)}
            label={`e2 = ${e2}`}
            color="var(--accent-soft)"
          />
        </>
      )}

      {/* Compact legend (color dots) */}
      <g transform={`translate(8, ${SIZE - 8})`}>
        <circle cx="4" cy="0" r="3.5" fill="var(--accent)" />
        <text x="12" y="3" fontSize="9" fill="var(--text-muted)" fontFamily="var(--font-body)">esquina</text>
        <circle cx="64" cy="0" r="3.5" fill="var(--rebar-longitudinal)" />
        <text x="72" y="3" fontSize="9" fill="var(--text-muted)" fontFamily="var(--font-body)">cara L1</text>
        <circle cx="120" cy="0" r="3.5" fill="var(--brand-violet)" />
        <text x="128" y="3" fontSize="9" fill="var(--text-muted)" fontFamily="var(--font-body)">cara L2</text>
      </g>
    </svg>
  );
}

/* ===================================================================
 *  Dimension chains and stirrup helpers
 * =================================================================== */

const DIM_LINE = 'var(--dim-line)';
const DIM_TEXT = 'var(--dim-text)';

/** Horizontal chain of segments + tick marks + spacing labels above/below */
function DimChain({
  orientation,
  anchor,
  positionsWorld,
  edgeMin,
  edgeMax,
  sx,
  scale,
}: {
  orientation: 'horizontal';
  anchor: number;                    // svg y of the dim line
  positionsWorld: number[];          // world-space x positions of the bars
  edgeMin: number;
  edgeMax: number;
  sx: (x: number) => number;
  scale: number;
}) {
  void orientation;
  if (positionsWorld.length < 2) return null;

  // Build the breakpoint set: edges + bars (deduped & sorted)
  const breaks = Array.from(
    new Set([edgeMin, ...positionsWorld, edgeMax].map((v) => Math.round(v * 1000) / 1000)),
  ).sort((a, b) => a - b);

  return (
    <g>
      {/* main line */}
      <line x1={sx(edgeMin)} y1={anchor} x2={sx(edgeMax)} y2={anchor} stroke={DIM_LINE} strokeWidth="0.6" />
      {/* ticks */}
      {breaks.map((b, i) => (
        <line key={i} x1={sx(b)} y1={anchor - 3} x2={sx(b)} y2={anchor + 3} stroke={DIM_LINE} strokeWidth="0.7" />
      ))}
      {/* spacings */}
      {breaks.slice(0, -1).map((b, i) => {
        const next = breaks[i + 1]!;
        const mid = (sx(b) + sx(next)) / 2;
        const len = next - b;
        if (len * scale < 14) return null;       // skip if too tight
        return (
          <text
            key={i}
            x={mid}
            y={anchor < 240 ? anchor - 5 : anchor + 11}
            textAnchor="middle"
            fontSize="9.5"
            fontFamily="var(--font-mono)"
            fill={DIM_TEXT}
          >
            {len.toFixed(1)}
          </text>
        );
      })}
    </g>
  );
}

/** Vertical chain, equivalent to DimChain but for left/right faces */
function DimChainV({
  anchor,
  positionsWorld,
  edgeMin,
  edgeMax,
  sy,
  scale,
}: {
  anchor: number;
  positionsWorld: number[];
  edgeMin: number;
  edgeMax: number;
  sy: (y: number) => number;
  scale: number;
}) {
  if (positionsWorld.length < 2) return null;

  const breaks = Array.from(
    new Set([edgeMin, ...positionsWorld, edgeMax].map((v) => Math.round(v * 1000) / 1000)),
  ).sort((a, b) => a - b);

  return (
    <g>
      <line x1={anchor} y1={sy(edgeMin)} x2={anchor} y2={sy(edgeMax)} stroke={DIM_LINE} strokeWidth="0.6" />
      {breaks.map((b, i) => (
        <line key={i} x1={anchor - 3} y1={sy(b)} x2={anchor + 3} y2={sy(b)} stroke={DIM_LINE} strokeWidth="0.7" />
      ))}
      {breaks.slice(0, -1).map((b, i) => {
        const next = breaks[i + 1]!;
        const mid = (sy(b) + sy(next)) / 2;
        const len = next - b;
        if (len * scale < 14) return null;
        return (
          <text
            key={i}
            x={anchor < 240 ? anchor - 5 : anchor + 5}
            y={mid}
            textAnchor={anchor < 240 ? 'end' : 'start'}
            fontSize="9.5"
            fontFamily="var(--font-mono)"
            fill={DIM_TEXT}
            dominantBaseline="middle"
          >
            {len.toFixed(1)}
          </text>
        );
      })}
    </g>
  );
}

/** Total dimension at the outer-most position with arrow ticks and label */
function DimTotal({
  orientation,
  anchor,
  from,
  to,
  label,
  color = DIM_LINE,
}: {
  orientation: 'horizontal' | 'vertical';
  anchor: number;
  from: number;
  to: number;
  label: string;
  color?: string;
}) {
  const isH = orientation === 'horizontal';
  const stroke = color;
  const text = color === DIM_LINE ? DIM_TEXT : color;
  if (isH) {
    return (
      <g>
        <line x1={from} y1={anchor} x2={to} y2={anchor} stroke={stroke} strokeWidth="0.8" />
        <line x1={from} y1={anchor - 5} x2={from} y2={anchor + 5} stroke={stroke} strokeWidth="0.8" />
        <line x1={to} y1={anchor - 5} x2={to} y2={anchor + 5} stroke={stroke} strokeWidth="0.8" />
        <text x={(from + to) / 2} y={anchor + (anchor < 240 ? -7 : 14)} textAnchor="middle" fontSize="11" fontFamily="var(--font-mono)" fill={text}>
          {label}
        </text>
      </g>
    );
  }
  return (
    <g>
      <line x1={anchor} y1={from} x2={anchor} y2={to} stroke={stroke} strokeWidth="0.8" />
      <line x1={anchor - 5} y1={from} x2={anchor + 5} y2={from} stroke={stroke} strokeWidth="0.8" />
      <line x1={anchor - 5} y1={to} x2={anchor + 5} y2={to} stroke={stroke} strokeWidth="0.8" />
      <text
        x={anchor + (anchor < 240 ? -8 : 8)}
        y={(from + to) / 2}
        textAnchor={anchor < 240 ? 'end' : 'start'}
        fontSize="11"
        fontFamily="var(--font-mono)"
        fill={text}
        dominantBaseline="middle"
      >
        {label}
      </text>
    </g>
  );
}

/** Stirrup polygon + 135° seismic hooks at every convex vertex */
function StirrupWithHooks({
  polygon,
  sx,
  sy,
  scale,
  hookLengthCm,
  noHooks = false,
}: {
  polygon: readonly (readonly [number, number])[];
  sx: (x: number) => number;
  sy: (y: number) => number;
  scale: number;
  hookLengthCm: number;
  noHooks?: boolean;
}) {
  const polyPts = polygon.map(([x, y]) => `${sx(x)},${sy(y)}`).join(' ');
  const hookLen = hookLengthCm * scale;
  const n = polygon.length;
  const hooks: React.ReactElement[] = [];
  if (noHooks) {
    // Circular ties / spirals don't have 135° seismic hooks at every vertex.
    return (
      <g>
        <polygon points={polyPts} fill="none" stroke="var(--rebar-stirrup)" strokeWidth="1" />
      </g>
    );
  }
  for (let i = 0; i < n; i++) {
    const prev = polygon[(i - 1 + n) % n]!;
    const cur = polygon[i]!;
    const next = polygon[(i + 1) % n]!;
    // Vectors leaving the vertex along the two edges (normalized)
    const e1x = prev[0] - cur[0];
    const e1y = prev[1] - cur[1];
    const e2x = next[0] - cur[0];
    const e2y = next[1] - cur[1];
    const l1 = Math.hypot(e1x, e1y) || 1;
    const l2 = Math.hypot(e2x, e2y) || 1;
    // Cross product to decide convex vs re-entrant for CCW polygon
    const cross = e1x * e2y - e1y * e2x;
    if (cross > 0) continue; // re-entrant — no hook
    // Hook direction: bisector of the two outgoing edges, OUTSIDE the polygon
    const bx = e1x / l1 + e2x / l2;
    const by = e1y / l1 + e2y / l2;
    const blen = Math.hypot(bx, by);
    if (blen < 1e-9) continue;
    // For a convex CCW vertex, the bisector of the OUTGOING edges points
    // inward; we negate it to point outward (where the hook extends).
    const dx = -bx / blen;
    const dy = -by / blen;
    const tipX = cur[0] + dx * hookLengthCm;
    const tipY = cur[1] + dy * hookLengthCm;
    hooks.push(
      <line
        key={i}
        x1={sx(cur[0])}
        y1={sy(cur[1])}
        x2={sx(tipX)}
        y2={sy(tipY)}
        stroke="var(--rebar-stirrup)"
        strokeWidth="1.2"
      />,
    );
    void hookLen;
  }
  return (
    <g>
      <polygon points={polyPts} fill="none" stroke="var(--rebar-stirrup)" strokeWidth="1" />
      {hooks}
    </g>
  );
}

/* ---------- L geometry helpers (centroid + raw vertices, centered) ---------- */
function lCentroid(L1: number, L2: number, e1: number, e2: number): [number, number] {
  const a1 = L1 * e1;
  const a2 = e2 * (L2 - e1);
  const at = a1 + a2;
  return [
    (a1 * (L1 / 2) + a2 * (e2 / 2)) / at,
    (a1 * (e1 / 2) + a2 * (e1 + (L2 - e1) / 2)) / at,
  ];
}

function lPolygonRaw(L1: number, L2: number, e1: number, e2: number): readonly (readonly [number, number])[] {
  const [cx, cy] = lCentroid(L1, L2, e1, e2);
  const raw: [number, number][] = [
    [0, 0],
    [L1, 0],
    [L1, e1],
    [e2, e1],
    [e2, L2],
    [0, L2],
  ];
  return raw.map(([x, y]) => [x - cx, y - cy] as const);
}

/** Circular polygon CCW (48-sided approximation), centered at the centroid.
 *  L1 = diameter. */
function circularPolygonRaw(L1: number): readonly (readonly [number, number])[] {
  const N = 48;
  const r = L1 / 2;
  const pts: [number, number][] = [];
  for (let i = 0; i < N; i++) {
    const a = (2 * Math.PI * i) / N;
    pts.push([r * Math.cos(a), r * Math.sin(a)]);
  }
  return pts;
}

/** Cruciform (+) polygon CCW, centered at the geometric centroid. */
function cruciformPolygonRaw(L1: number, L2: number, e1: number, e2: number): readonly (readonly [number, number])[] {
  const cx = L1 / 2;
  const cy = L2 / 2;
  const xwL = (L1 - e2) / 2;
  const xwR = (L1 + e2) / 2;
  const yhB = (L2 - e1) / 2;
  const yhT = (L2 + e1) / 2;
  const raw: [number, number][] = [
    [xwL, 0],
    [xwR, 0],
    [xwR, yhB],
    [L1, yhB],
    [L1, yhT],
    [xwR, yhT],
    [xwR, L2],
    [xwL, L2],
    [xwL, yhT],
    [0, yhT],
    [0, yhB],
    [xwL, yhB],
  ];
  return raw.map(([x, y]) => [x - cx, y - cy] as const);
}

/** T-shape polygon CCW, centered at the geometric centroid. */
function tPolygonRaw(L1: number, L2: number, e1: number, e2: number): readonly (readonly [number, number])[] {
  const aFl = L1 * e1;
  const aWe = e2 * (L2 - e1);
  const at = aFl + aWe;
  const cx = L1 / 2;                             // symmetric in x
  const cy = (aFl * (L2 - e1 / 2) + aWe * ((L2 - e1) / 2)) / at;
  const xwL = (L1 - e2) / 2;
  const xwR = (L1 + e2) / 2;
  const yfB = L2 - e1;
  const raw: [number, number][] = [
    [xwL, 0],
    [xwR, 0],
    [xwR, yfB],          // re-entrant
    [L1, yfB],
    [L1, L2],
    [0, L2],
    [0, yfB],
    [xwL, yfB],          // re-entrant
  ];
  return raw.map(([x, y]) => [x - cx, y - cy] as const);
}

/** In-place inset (offset toward interior) using line intersection — same
 *  algorithm as the 3D viewer. */
function insetPolygonInPlace(
  poly: readonly (readonly [number, number])[],
  inset: number,
): readonly (readonly [number, number])[] {
  const n = poly.length;
  type L = { px: number; py: number; dx: number; dy: number };
  const lines: L[] = [];
  for (let i = 0; i < n; i++) {
    const a = poly[i]!;
    const b = poly[(i + 1) % n]!;
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len = Math.hypot(dx, dy) || 1;
    // Inward normal for CCW polygon = +90° rotation = (-dy, dx)/len.
    const nx = -dy / len;
    const ny = dx / len;
    lines.push({ px: a[0] + nx * inset, py: a[1] + ny * inset, dx, dy });
  }
  const out: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const lp = lines[(i - 1 + n) % n]!;
    const lc = lines[i]!;
    const det = -lp.dx * lc.dy + lp.dy * lc.dx;
    if (Math.abs(det) < 1e-9) {
      out.push([lp.px + lp.dx, lp.py + lp.dy]);
      continue;
    }
    const t = (-(lc.px - lp.px) * lc.dy + (lc.py - lp.py) * lc.dx) / det;
    out.push([lp.px + t * lp.dx, lp.py + t * lp.dy]);
  }
  return out;
}

/* ---------- L dimension lines (L1 bottom, L2 left, e1 right, e2 top) ---------- */
function LDimensions({
  L1, L2, e1, e2, cx, cy, scale,
}: {
  L1: number; L2: number; e1: number; e2: number; cx: number; cy: number; scale: number;
}) {
  const [ccx, ccy] = lCentroid(L1, L2, e1, e2);
  // Convert raw (x_raw, y_raw) → SVG (svg_x, svg_y)
  const sx = (x: number) => cx + (x - ccx) * scale;
  const sy = (y: number) => cy - (y - ccy) * scale;

  // L1 dimension along the bottom (full width)
  const xL1_a = sx(0);
  const xL1_b = sx(L1);
  const yL1_dim = sy(0) + 30;

  // L2 dimension along the left (full height)
  const yL2_a = sy(0);
  const yL2_b = sy(L2);
  const xL2_dim = sx(0) - 32;

  // e1 dimension (height of the bottom flange) on the RIGHT side, between y=0 and y=e1
  const xE1_dim = sx(L1) + 32;
  const yE1_a = sy(0);
  const yE1_b = sy(e1);

  // e2 dimension (width of the left web) on the TOP, between x=0 and x=e2
  const yE2_dim = sy(L2) - 24;
  const xE2_a = sx(0);
  const xE2_b = sx(e2);

  return (
    <g>
      {/* L1 — bottom */}
      <line x1={xL1_a} y1={yL1_dim} x2={xL1_b} y2={yL1_dim} stroke="var(--dim-line)" strokeWidth="1" />
      <line x1={xL1_a} y1={yL1_dim - 4} x2={xL1_a} y2={yL1_dim + 4} stroke="var(--dim-line)" />
      <line x1={xL1_b} y1={yL1_dim - 4} x2={xL1_b} y2={yL1_dim + 4} stroke="var(--dim-line)" />
      <text x={(xL1_a + xL1_b) / 2} y={yL1_dim + 14} textAnchor="middle" fontSize="11" fontFamily="var(--font-mono)" fill="var(--dim-text)">
        L1 = {L1} cm
      </text>

      {/* L2 — left */}
      <line x1={xL2_dim} y1={yL2_a} x2={xL2_dim} y2={yL2_b} stroke="var(--dim-line)" strokeWidth="1" />
      <line x1={xL2_dim - 4} y1={yL2_a} x2={xL2_dim + 4} y2={yL2_a} stroke="var(--dim-line)" />
      <line x1={xL2_dim - 4} y1={yL2_b} x2={xL2_dim + 4} y2={yL2_b} stroke="var(--dim-line)" />
      <text x={xL2_dim - 6} y={(yL2_a + yL2_b) / 2} textAnchor="end" fontSize="11" fontFamily="var(--font-mono)" fill="var(--dim-text)" dominantBaseline="middle">
        L2 = {L2} cm
      </text>

      {/* e1 — right side (flange thickness) */}
      <line x1={xE1_dim} y1={yE1_a} x2={xE1_dim} y2={yE1_b} stroke="var(--accent-soft)" strokeWidth="1" />
      <line x1={xE1_dim - 4} y1={yE1_a} x2={xE1_dim + 4} y2={yE1_a} stroke="var(--accent-soft)" />
      <line x1={xE1_dim - 4} y1={yE1_b} x2={xE1_dim + 4} y2={yE1_b} stroke="var(--accent-soft)" />
      <text x={xE1_dim + 6} y={(yE1_a + yE1_b) / 2} textAnchor="start" fontSize="10" fontFamily="var(--font-mono)" fill="var(--accent-soft)" dominantBaseline="middle">
        e1 = {e1}
      </text>

      {/* e2 — top (web thickness) */}
      <line x1={xE2_a} y1={yE2_dim} x2={xE2_b} y2={yE2_dim} stroke="var(--accent-soft)" strokeWidth="1" />
      <line x1={xE2_a} y1={yE2_dim - 4} x2={xE2_a} y2={yE2_dim + 4} stroke="var(--accent-soft)" />
      <line x1={xE2_b} y1={yE2_dim - 4} x2={xE2_b} y2={yE2_dim + 4} stroke="var(--accent-soft)" />
      <text x={(xE2_a + xE2_b) / 2} y={yE2_dim - 4} textAnchor="middle" fontSize="10" fontFamily="var(--font-mono)" fill="var(--accent-soft)">
        e2 = {e2}
      </text>
    </g>
  );
}

function DimX({ cx, cy, length, value }: { cx: number; cy: number; length: number; value: number }) {
  const x1 = cx - length / 2;
  const x2 = cx + length / 2;
  return (
    <g>
      <line x1={x1} y1={cy} x2={x2} y2={cy} stroke="var(--dim-line)" strokeWidth="1" />
      <line x1={x1} y1={cy - 4} x2={x1} y2={cy + 4} stroke="var(--dim-line)" />
      <line x1={x2} y1={cy - 4} x2={x2} y2={cy + 4} stroke="var(--dim-line)" />
      <text x={cx} y={cy + 16} textAnchor="middle" fontSize="11" fontFamily="var(--font-mono)" fill="var(--dim-text)">
        L1 = {value} cm
      </text>
    </g>
  );
}

function DimY({ cx, cy, length, value }: { cx: number; cy: number; length: number; value: number }) {
  const y1 = cy - length / 2;
  const y2 = cy + length / 2;
  return (
    <g>
      <line x1={cx} y1={y1} x2={cx} y2={y2} stroke="var(--dim-line)" strokeWidth="1" />
      <line x1={cx - 4} y1={y1} x2={cx + 4} y2={y1} stroke="var(--dim-line)" />
      <line x1={cx - 4} y1={y2} x2={cx + 4} y2={y2} stroke="var(--dim-line)" />
      <text
        x={cx - 10}
        y={cy}
        textAnchor="middle"
        transform={`rotate(-90 ${cx - 10} ${cy})`}
        fontSize="11"
        fontFamily="var(--font-mono)"
        fill="var(--dim-text)"
      >
        L2 = {value} cm
      </text>
    </g>
  );
}

function ParametersDock() {
  return (
    <div
      className="absolute inset-x-4 bottom-4 grid grid-cols-3 gap-x-6 gap-y-3 rounded-[var(--radius-md)] border p-4"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
    >
      <div className="col-span-3 mb-1 text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
        Parametros de calculo y visualizacion 3D
      </div>
      <SliderRow label="Inc. Angular (a)" value="10" suffix="°" />
      <SliderRow label="N Divisiones" value="30" />
      <SliderRow label="Escala (Eje P)" value="0.61" />
      <SliderRow label="Tamano puntos" value="3.2" />
      <div className="flex items-center justify-between">
        <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
          Tipo de Vista
        </span>
        <select
          className="h-7 rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-2 text-[11px] font-medium text-[var(--text-primary)]"
          style={{ borderColor: 'var(--border-default)' }}
          defaultValue="malla"
        >
          <option value="malla">Malla</option>
          <option value="solido">Solido</option>
          <option value="puntos">Puntos</option>
        </select>
      </div>
    </div>
  );
}

function SliderRow({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-32 shrink-0 text-[11px]" style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <input type="range" defaultValue={Number(value) || 0} min="0" max="100" className="flex-1 accent-[var(--accent)]" />
      <span className="w-12 text-right font-mono tabular text-[11px]" style={{ color: 'var(--text-primary)' }}>
        {value}{suffix ?? ''}
      </span>
    </div>
  );
}

function RightRail({
  mode,
  outputs,
  stirrups,
  shear,
  flexocomp,
  dcRatio,
  verifications,
  isFetching,
  onExportPdf,
  onExportXlsx,
  exportBusy,
}: {
  mode: 'design' | 'analysis';
  outputs?: { Ag: number; As: number; As_min: number; As_max: number; rho: number; n_bars: number; bars_tag: string };
  stirrups?: import('@/features/columns/types').StirrupOutputs;
  shear?: import('@/features/columns/types').ShearOutputs;
  flexocomp?: import('@/features/columns/types').FlexocompressionCheck;
  dcRatio?: number;
  verifications: Verification[];
  isFetching: boolean;
  onExportPdf: () => void;
  onExportXlsx: () => void;
  exportBusy: 'pdf' | 'xlsx' | null;
}) {
  const dc = dcRatio ?? 0;
  const dcTone = dc > 1 ? 'danger' : dc > 0.95 ? 'warning' : 'success';
  const dcColor =
    dcTone === 'success' ? 'var(--success)' : dcTone === 'warning' ? 'var(--warning)' : 'var(--danger)';

  return (
    <aside
      className="flex shrink-0 flex-col overflow-y-auto border-l p-4 scrollbar-thin"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)', width: 360 }}
    >
      {mode === 'analysis' && outputs && (
        <div
          className="mb-4 rounded-[var(--radius-md)] border p-4"
          style={{
            background: 'var(--surface-2)',
            borderColor: 'var(--border-default)',
            borderLeft: `3px solid ${dcColor}`,
            transition: 'opacity 200ms',
            opacity: isFetching ? 0.7 : 1,
          }}
        >
          <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
            Ratio D/C
          </div>
          <div className="mt-1 font-mono tabular text-4xl" style={{ color: dcColor, lineHeight: 1 }}>
            {dc.toFixed(3)}
          </div>
          <div className="mt-2 text-[11px]" style={{ color: 'var(--text-muted)' }}>
            {dc <= 1 ? 'OK - dentro del envolvente de diseno.' : 'Excede capacidad. Aumentar As o seccion.'}
          </div>
        </div>
      )}

      {flexocomp && (
        <div
          className="mb-4 rounded-[var(--radius-md)] border p-3"
          style={{
            background: 'var(--surface-2)',
            borderColor: 'var(--border-default)',
            borderLeft: `3px solid ${flexocomp.is_flexocompression ? 'var(--brand-alt)' : 'var(--accent)'}`,
          }}
        >
          <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
            Verificacion inicial (E.060 21.4.5)
          </div>
          <div
            className="mt-1 text-[12.5px]"
            style={{
              color: flexocomp.is_flexocompression ? 'var(--brand-alt)' : 'var(--accent)',
              fontWeight: 700,
            }}
          >
            {flexocomp.is_flexocompression ? 'FLEXOCOMPRESION' : 'FLEXION (no es columna pura)'}
          </div>
          <div className="mt-1 font-mono tabular text-[10.5px]" style={{ color: 'var(--text-secondary)' }}>
            Pu = {flexocomp.Pu.toLocaleString()} kgf
            <br />
            0.1 f'c Ag = {flexocomp.threshold.toLocaleString()} kgf
          </div>
        </div>
      )}

      <SectionHeading icon={<Lock size={11} strokeWidth={2} />} label="Resultados de la seccion" hint="calculados" />

      <div
        className="mb-4 grid grid-cols-2 gap-2"
        style={{ opacity: isFetching ? 0.7 : 1, transition: 'opacity 200ms' }}
      >
        <Output label="Ag" help={HELP.Ag} value={outputs ? outputs.Ag.toFixed(0) : '—'} unit="cm²" />
        <Output label="As_min" help={HELP.As_min} value={outputs ? outputs.As_min.toFixed(2) : '—'} unit="cm²" />
        <Output label="#Varillas" help={HELP.bars_tag} value={outputs?.bars_tag ?? '—'} mono />
        <Output
          label="As"
          help={HELP.As}
          value={outputs ? outputs.As.toFixed(2) : '—'}
          unit="cm²"
          tone={outputs && outputs.As < outputs.As_min ? 'warning' : 'success'}
        />
        <Output
          label="ρ"
          help={HELP.rho}
          value={outputs ? (outputs.rho * 100).toFixed(2) : '—'}
          unit="%"
          tone={outputs && outputs.rho < 0.01 ? 'warning' : 'success'}
        />
      </div>

      {outputs && (
        <div
          className="mb-4 rounded-[var(--radius-md)] border p-3"
          style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)' }}
        >
          <CuantiaBar rho={outputs.rho} />
        </div>
      )}

      {stirrups && (
        <>
          <SectionHeading icon={<Lock size={11} strokeWidth={2} />} label="Estribos (E.060 21.4.4)" hint="calculados" />
          <div
            className="mb-4 grid grid-cols-2 gap-2"
            style={{ opacity: isFetching ? 0.7 : 1, transition: 'opacity 200ms' }}
          >
            <Output
              label="Ø estribo"
              value={`${stirrups.diameter_in}"`}
              mono
            />
            <Output
              label="# total"
              help={HELP.totalStirrups}
              value={stirrups.n_total.toString()}
              mono
            />
            <Output
              label="s confinam."
              help={HELP.stirrupSpacingConfinement}
              value={stirrups.spacing_confinement.toFixed(1)}
              unit="cm"
            />
            <Output
              label="s central"
              help={HELP.stirrupSpacingCentral}
              value={stirrups.spacing_central.toFixed(1)}
              unit="cm"
            />
            <Output
              label="Lo (extremo)"
              help={HELP.confinementLength}
              value={stirrups.confinement_length.toFixed(0)}
              unit="cm"
            />
            <Output
              label="Acero estribo"
              help={HELP.totalStirrupLength}
              value={(stirrups.total_steel_length / 100).toFixed(2)}
              unit="m"
            />
          </div>
          <div
            className="mb-4 rounded-[var(--radius-sm)] border p-2.5 text-[10.5px]"
            style={{
              background: 'var(--surface-2)',
              borderColor: 'var(--border-default)',
              color: 'var(--text-muted)',
              lineHeight: 1.45,
            }}
          >
            <span style={{ color: 'var(--text-secondary)' }}>Distribucion: </span>
            {stirrups.n_confinement_each_end} {`@ ${stirrups.spacing_confinement.toFixed(1)} cm`} en
            cada extremo (Lo = {stirrups.confinement_length.toFixed(0)} cm), {stirrups.n_central}{' '}
            {`@ ${stirrups.spacing_central.toFixed(1)} cm`} en zona central. Ganchos sismicos a 135°.
          </div>
        </>
      )}

      {shear && (shear.eje_x.Vu > 0 || shear.eje_y.Vu > 0) && (
        <>
          <SectionHeading icon={<Lock size={11} strokeWidth={2} />} label="Cortante (E.060 11.3)" hint="calculados" />
          <div
            className="mb-4 rounded-[var(--radius-md)] border p-3 text-[11px]"
            style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}
          >
            <table className="w-full" style={{ tableLayout: 'fixed' }}>
              <thead>
                <tr style={{ color: 'var(--text-muted)' }} className="text-[9.5px] uppercase tracking-wider">
                  <th className="pb-1 text-left">Eje</th>
                  <th className="pb-1 text-right">Vu</th>
                  <th className="pb-1 text-right">Vc</th>
                  <th className="pb-1 text-right">Vs req.</th>
                  <th className="pb-1 text-right">Ramas</th>
                </tr>
              </thead>
              <tbody className="font-mono tabular">
                <tr>
                  <td className="py-0.5 text-[var(--text-primary)]">X</td>
                  <td className="py-0.5 text-right">{(shear.eje_x.Vu / 1000).toFixed(1)}k</td>
                  <td className="py-0.5 text-right">{(shear.eje_x.Vc / 1000).toFixed(1)}k</td>
                  <td className="py-0.5 text-right" style={{ color: shear.eje_x.Vs_required > 0 ? 'var(--warning)' : 'var(--success)' }}>
                    {shear.eje_x.Vs_required > 0 ? `${(shear.eje_x.Vs_required / 1000).toFixed(1)}k` : '0'}
                  </td>
                  <td className="py-0.5 text-right">{shear.eje_x.n_legs_recommended}</td>
                </tr>
                <tr>
                  <td className="py-0.5 text-[var(--text-primary)]">Y</td>
                  <td className="py-0.5 text-right">{(shear.eje_y.Vu / 1000).toFixed(1)}k</td>
                  <td className="py-0.5 text-right">{(shear.eje_y.Vc / 1000).toFixed(1)}k</td>
                  <td className="py-0.5 text-right" style={{ color: shear.eje_y.Vs_required > 0 ? 'var(--warning)' : 'var(--success)' }}>
                    {shear.eje_y.Vs_required > 0 ? `${(shear.eje_y.Vs_required / 1000).toFixed(1)}k` : '0'}
                  </td>
                  <td className="py-0.5 text-right">{shear.eje_y.n_legs_recommended}</td>
                </tr>
              </tbody>
            </table>
            <div className="mt-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>
              Vc incluye factor por compresión axial. φ = 0.85. Ramas = ramales recomendados por la
              dirección, según espaciamiento transversal máximo de 15 cm.
            </div>
          </div>
        </>
      )}

      {verifications.length > 0 && (
        <div className="mb-4">
          <div className="mb-2 text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
            Verificaciones
          </div>
          <ul className="space-y-1">
            {verifications.map((v) => (
              <li
                key={v.id}
                className="flex items-center justify-between rounded-[var(--radius-sm)] border px-2 py-1 text-[11px]"
                style={{
                  background: 'var(--surface-2)',
                  borderColor: 'var(--border-default)',
                  color: 'var(--text-secondary)',
                }}
              >
                <span className="flex items-center gap-1.5">
                  {v.ok ? (
                    <CheckCircle2 size={12} strokeWidth={2} style={{ color: 'var(--success)' }} />
                  ) : (
                    <AlertCircle size={12} strokeWidth={2} style={{ color: 'var(--danger)' }} />
                  )}
                  {v.label}
                </span>
                <span className="font-mono tabular" style={{ color: v.ok ? 'var(--success)' : 'var(--danger)' }}>
                  {v.value < 0.01 ? v.value.toExponential(2) : v.value.toFixed(3)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-2 grid grid-cols-2 gap-2">
        <Button variant="secondary" size="sm" onClick={onExportPdf} disabled={exportBusy === 'pdf'}>
          {exportBusy === 'pdf' ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} strokeWidth={1.8} />}
          PDF
          <ExportCostHint cost={2} />
        </Button>
        <Button variant="secondary" size="sm" onClick={onExportXlsx} disabled={exportBusy === 'xlsx'}>
          {exportBusy === 'xlsx' ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} strokeWidth={1.8} />}
          Excel
          <ExportCostHint cost={2} />
        </Button>
      </div>
    </aside>
  );
}

function SectionHeading({
  icon,
  label,
  hint,
}: {
  icon?: React.ReactNode;
  label: string;
  hint?: string;
}) {
  return (
    <div className="mb-2 flex items-center gap-1.5 text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
      {icon && <span style={{ color: 'var(--text-disabled)' }}>{icon}</span>}
      <span>{label}</span>
      {hint && (
        <span
          className="ml-auto rounded-[var(--radius-pill)] border px-1.5 py-0 text-[8.5px] normal-case tracking-normal"
          style={{ borderColor: 'var(--border-default)', color: 'var(--text-disabled)' }}
        >
          {hint}
        </span>
      )}
    </div>
  );
}

function Output({
  label,
  help,
  value,
  unit,
  tone,
  mono,
}: {
  label: string;
  help?: string;
  value: string;
  unit?: string;
  tone?: 'success' | 'warning' | 'danger';
  mono?: boolean;
}) {
  const toneColor =
    tone === 'success' ? 'var(--success)' : tone === 'warning' ? 'var(--warning)' : tone === 'danger' ? 'var(--danger)' : 'var(--text-primary)';
  return (
    <div className="rounded-[var(--radius-sm)] border p-2" style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)' }}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
        {label}
        {help && <InfoTooltip content={help} side="left" />}
      </div>
      <div className="mt-1 flex items-baseline gap-1">
        <span
          className={mono ? 'font-mono tabular text-sm' : 'font-mono tabular text-base'}
          style={{ color: toneColor, lineHeight: 1 }}
        >
          {value}
        </span>
        {unit && (
          <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}
